import "reflect-metadata";
import { Container } from "inversify";
import {
  UserRepo,
  UserRepositoryUID,
} from "@linkfluence/core/user/repositories/UserRepo";
import { AiAPI } from "@radarly/api/ai";
import { AichaAPI } from "@radarly/api/aicha";
import { BlacklistAPI } from "@radarly/api/blacklist";
import { BlacklistQueriesAPI } from "@radarly/api/blacklist-queries";
import { ClauseAPI } from "@radarly/api/clause";
import { GeoAPI } from "@radarly/api/geo";
import { InfluencerAPI } from "@radarly/api/influencer";
import { InsightPagesAPI } from "@radarly/api/insight-pages";
import { InsightsAPI } from "@radarly/api/insights";
import { MemberAPI } from "@radarly/api/member";
import { OccupationsAPI } from "@radarly/api/occupations";
import { ProjectAPI } from "@radarly/api/project";
import { QueryAPI } from "@radarly/api/queries";
import { RolesAPI } from "@radarly/api/roles";
import { WarmupAPI } from "@radarly/api/warmups";
import { WorkspaceAPI } from "@radarly/api/workspace";
import {
  InsightsRepository,
  InsightsRepositoryUID,
} from "@radarly/core/insights/repositories/InsightsRepository";
import { RESTInsightsRepository } from "@radarly/core/insights/repositories/RESTInsightsRepository";
import { APISearchRepository } from "@radarly/core/search/repositories/APISearchRepository";
import { ConnectedUserRepo } from "@radarly/core/user/repositories/ConnectedUserRepo";
import { AiRepo, AiRepositoryUID } from "./ai/repositories/AiRepo";
import { ConnectedAiRepo } from "./ai/repositories/ConnectedAiRepo";
import { AichaRepo, AichaRepositoryUID } from "./aicha/repositories/AichaRepo";
import { ConnectedAichaRepo } from "./aicha/repositories/ConnectedAichaRepo";
import {
  BlacklistQueriesRepository,
  BlacklistQueriesRepositoryUID,
} from "./blacklist-queries/repositories/BlacklistQueriesRepository";
import { ConnectedBlacklistQueriesRepository } from "./blacklist-queries/repositories/ConnectedBlacklistQueriesRepository";
import {
  BlacklistRepository,
  BlacklistRepositoryUID,
} from "./blacklist/repositories/BlacklistRepository";
import { ConnectedBlacklistRepository } from "./blacklist/repositories/ConnectedBlacklistRepository";
import {
  ClauseRepo,
  ClauseRepositoryUID,
} from "./clause/repositories/ClauseRepo";
import { ConnectedClauseRepo } from "./clause/repositories/ConnectedClauseRepo";
import { ConnectedCorporaRepo } from "./corpora/repositories/ConnectedCorporaRepo";
import {
  CorporaRepo,
  CorporaRepositoryUID,
} from "./corpora/repositories/CorporaRepo";
import { ConnectedInfluencerRepository } from "./influencer/repositories/ConnectedInfluencerRepository";
import {
  InfluencerRepository,
  InfluencerRepositoryUID,
} from "./influencer/repositories/InfluencerRepository";
import {
  InsightPageRepo,
  InsightPageRepositoryUID,
} from "./insight-pages/repositories/InsightPageRepo";
import { RESTInsightPageRepo } from "./insight-pages/repositories/RESTInsightPageRepo";
import { ConnectedMemberRepo } from "./member/repositories/ConnectedMemberRepo";
import { MemberRepo, MemberRepoUID } from "./member/repositories/MemberRepo";
import { ConnectedProductRepo } from "./products/ConnectedProductRepo";
import { ProductRepo, ProductRepositoryUID } from "./products/ProductRepo";
import { ConnectedProjectRepo } from "./project/repositories/ConnectedProjectRepo";
import {
  ProjectRepo,
  ProjectRepositoryUID,
} from "./project/repositories/ProjectRepo";
import { APIQueryRepository } from "./queries/repositories/APIQueryRepository";
import { APIWarmupRepository } from "./queries/repositories/APIWarmupRepository";
import {
  QueryRepository,
  QueryRepositoryUID,
} from "./queries/repositories/QueryRepository";
import {
  WarmupRepository,
  WarmupRepositoryUID,
} from "./queries/repositories/WarmupRepository";
import { ConnectedRolesRepo } from "./roles/repositories/ConnectedRolesRepo";
import { RolesRepo, RolesRepoUID } from "./roles/repositories/RolesRepo";
import {
  SearchRepository,
  SearchRepositoryUID,
} from "./search/repositories/SearchRepository";
import { APIWorkspaceRepository } from "./workspace/repositories/APIWorkspaceRepository";
import {
  WorkspaceRepository,
  WorkspaceRepositoryUID,
} from "./workspace/repositories/WorkspaceRepository";

export function getContainer() {
  const container = new Container();
  const searchRepository = new APISearchRepository();
  container
    .bind<SearchRepository>(SearchRepositoryUID)
    .toConstantValue(searchRepository);
  container
    .bind<UserRepo>(UserRepositoryUID)
    .toConstantValue(new ConnectedUserRepo());
  container.bind<QueryRepository>(QueryRepositoryUID).toConstantValue(
    new APIQueryRepository({
      queryAPI: new QueryAPI(),
    })
  );
  container.bind<WarmupRepository>(WarmupRepositoryUID).toConstantValue(
    new APIWarmupRepository({
      warmupAPI: new WarmupAPI(),
    })
  );
  container.bind<ProjectRepo>(ProjectRepositoryUID).toConstantValue(
    new ConnectedProjectRepo({
      projectAPI: new ProjectAPI(),
    })
  );
  container.bind<WorkspaceRepository>(WorkspaceRepositoryUID).toConstantValue(
    new APIWorkspaceRepository({
      workspaceAPI: new WorkspaceAPI(),
    })
  );
  container.bind<BlacklistRepository>(BlacklistRepositoryUID).toConstantValue(
    new ConnectedBlacklistRepository({
      blacklistAPI: new BlacklistAPI(),
    })
  );
  container
    .bind<BlacklistQueriesRepository>(BlacklistQueriesRepositoryUID)
    .toConstantValue(
      new ConnectedBlacklistQueriesRepository({
        blacklistQueriesAPI: new BlacklistQueriesAPI(),
      })
    );
  container.bind<InfluencerRepository>(InfluencerRepositoryUID).toConstantValue(
    new ConnectedInfluencerRepository({
      influencerAPI: new InfluencerAPI(),
    })
  );
  container.bind<MemberRepo>(MemberRepoUID).toConstantValue(
    new ConnectedMemberRepo({
      memberAPI: new MemberAPI(),
    })
  );
  container.bind<InsightPageRepo>(InsightPageRepositoryUID).toConstantValue(
    new RESTInsightPageRepo({
      insightPagesAPI: new InsightPagesAPI(),
    })
  );
  container.bind<ClauseRepo>(ClauseRepositoryUID).toConstantValue(
    new ConnectedClauseRepo({
      clauseAPI: new ClauseAPI({
        occupationsAPI: new OccupationsAPI(),
        geoAPI: new GeoAPI(),
      }),
    })
  );
  container
    .bind<ProductRepo>(ProductRepositoryUID)
    .toConstantValue(
      new ConnectedProductRepo({ searchRepo: searchRepository })
    );
  container
    .bind<CorporaRepo>(CorporaRepositoryUID)
    .toConstantValue(new ConnectedCorporaRepo());

  container
    .bind<InsightsRepository>(InsightsRepositoryUID)
    .toConstantValue(
      new RESTInsightsRepository({ insightsAPI: new InsightsAPI() })
    );
  container.bind<RolesRepo>(RolesRepoUID).toConstantValue(
    new ConnectedRolesRepo({
      rolesAPI: new RolesAPI(),
    })
  );
  container.bind<AichaRepo>(AichaRepositoryUID).toConstantValue(
    new ConnectedAichaRepo({
      aichaAPI: new AichaAPI(),
    })
  );
  container.bind<AiRepo>(AiRepositoryUID).toConstantValue(
    new ConnectedAiRepo({
      ai: new AiAPI(),
    })
  );

  return container;
}
